import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import dayjs from "dayjs";
import { Bell, Crown, FileText, FileSearch, Landmark, Vote, BadgeCheck, Globe, LineChart, Database } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useArticlePath } from "@/lib/i18n";
import { PANEL, PANEL_STYLE, CARD_STYLE, Flag, InfoTip, AreaSpark, STAGE_META, IMPACT_META, LEVELS, deltaTone } from "./regShared";

const KIND_COLORS: Record<string, string> = {
  "Policy Proposal": "#c36bff", Enforcement: "#ff5bc8", Guidance: "#14cfe6",
  Consultation: "#f5b420", "Court Ruling": "#ff8a5c", "Regulatory Update": "#7d9dd8",
};

export const AlertsRail = ({ alerts }: { alerts: any[] }) => {
  const toArticle = useArticlePath();
  return (
    <div data-testid="reg-alerts" className={`${PANEL} p-5 mt-5`} style={{ ...PANEL_STYLE, background: "#000000" }}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <Bell className="w-4 h-4 text-[#b88cff]" aria-hidden />
          <h3 className="nl-geist font-bold text-[13px] text-[#eef1f9] tracking-[0.04em]">LATEST REGULATORY ALERTS</h3>
          <InfoTip label="How alerts are sourced" testid="reg-alerts-info">
            The six most recent policy stories from NewsLayer coverage. Alert types
            (Enforcement, Guidance, Consultation…) are derived from story content.
          </InfoTip>
        </div>
        <Link to="/policy" data-testid="reg-alerts-view-all"
          className="nl-geist font-semibold text-[11px] text-[#14cfe6] hover:text-white motion-safe:transition-colors whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#14cfe6] rounded">
          View all alerts →
        </Link>
      </div>
      {alerts.length === 0 ? (
        <p className="nl-geist text-[11px] text-[#4c5670] py-6">No policy alerts available yet — coverage updates continuously.</p>
      ) : (
        <div className="mt-4 grid grid-flow-col auto-cols-[216px] gap-4 overflow-x-auto snap-x snap-mandatory pb-1 xl:grid-flow-row xl:grid-cols-6 xl:auto-cols-auto xl:overflow-visible no-scrollbar max-w-full">
          {alerts.map((a, i) => (
            <Link key={a.slug + i} to={toArticle(a.slug)} data-testid={`reg-alert-${i}`}
              className="group snap-start rounded-xl ring-1 ring-white/[0.08] p-3.5 min-h-[112px] flex flex-col hover:ring-[#8b5cff]/80 hover:shadow-[0_10px_30px_rgba(107,56,255,0.18)] motion-safe:transition-[box-shadow] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8b5cff]"
              style={CARD_STYLE}>
              <span className="flex items-center gap-2">
                <Flag code={a.code} className="w-[26px] h-[18px]" />
                <span className="nl-geist font-bold text-[9px] tracking-[0.1em] truncate text-[#9aa6c4]">
                  {a.jurisdiction.toUpperCase()}
                </span>
              </span>
              <span className="nl-geist font-semibold text-[11.5px] leading-[1.35] text-[#eef1f9] line-clamp-3 mt-2 group-hover:text-white">
                {a.title}
              </span>
              <span className="nl-geist text-[9px] mt-auto pt-2" style={{ color: KIND_COLORS[a.kind] || "#7d9dd8" }}>
                {dayjs(a.published_at).fromNow()} · {a.kind}
              </span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

type SortKey = "updated" | "jurisdiction" | "impact";
const IMPACT_RANK: Record<string, number> = { critical: 3, high: 2, medium: 1, low: 0 };
const STAGE_ICONS: Record<string, any> = {
  proposed: FileText, in_review: FileSearch, in_committee: Landmark, voting: Vote, passed: BadgeCheck,
};

export const PipelinePanel = ({ pipeline }: { pipeline: any }) => {
  const [filter, setFilter] = useState<string | null>(null);
  const [sort, setSort] = useState<{ key: SortKey; dir: 1 | -1 }>({ key: "updated", dir: -1 });
  const stages = pipeline?.stages || [];
  const allRows = useMemo(() => pipeline?.rows || [], [pipeline]);

  const rows = useMemo(() => {
    let out = filter ? allRows.filter((r: any) => r.stage === filter) : [...allRows];
    out.sort((a: any, b: any) => {
      if (sort.key === "updated") return (a.updated_at || "").localeCompare(b.updated_at || "") * sort.dir;
      if (sort.key === "jurisdiction") return a.jurisdiction.localeCompare(b.jurisdiction) * sort.dir;
      return ((IMPACT_RANK[a.impact] ?? 0) - (IMPACT_RANK[b.impact] ?? 0)) * sort.dir;
    });
    return out;
  }, [allRows, filter, sort]);

  const toggleSort = (key: SortKey) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === 1 ? -1 : 1 } : { key, dir: -1 }));
  const arrow = (key: SortKey) => (sort.key === key ? (sort.dir === -1 ? " ↓" : " ↑") : "");
  const TH = "nl-geist font-bold text-[9px] tracking-[0.1em] text-[#6a7690]";

  return (
    <div data-testid="reg-pipeline" className={`${PANEL} p-5 mt-5`} style={{ ...PANEL_STYLE, background: "#000000" }}>
      <div className="flex items-center gap-2">
        <h3 className="nl-geist font-bold text-[15px] text-[#eef1f9] tracking-[0.04em]">REGULATORY PIPELINE OVERVIEW</h3>
        <InfoTip label="Pipeline methodology" testid="reg-pipeline-info">
          Regulations tracked by the NewsLayer regulation desk, staged by their official
          status. Select a stage to filter the table below.
        </InfoTip>
      </div>

      <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-4" role="group" aria-label="Pipeline stage filters">
        {stages.map((s: any) => {
          const meta = STAGE_META[s.key];
          const Icon = STAGE_ICONS[s.key];
          const active = filter === s.key;
          return (
            <button key={s.key} type="button" data-testid={`reg-stage-${s.key}`}
              aria-pressed={active}
              onClick={() => setFilter(active ? null : s.key)}
              className={`text-left rounded-xl p-3.5 motion-safe:transition-[box-shadow,background-color] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8b5cff] ${active ? "" : "hover:brightness-125"}`}
              style={{
                background: active
                  ? `linear-gradient(180deg, ${meta.color}24 0%, #060609 62%)`
                  : "linear-gradient(180deg, #0a0a10 0%, #050507 100%)",
                boxShadow: active
                  ? `inset 0 0 0 1px ${meta.color}d9, 0 10px 32px ${meta.color}2e`
                  : `inset 0 0 0 1px ${meta.color}3d`,
              }}>
              <span className="flex items-center gap-2.5">
                <span className="w-[34px] h-[34px] rounded-[10px] flex items-center justify-center shrink-0"
                  style={{ backgroundColor: `${meta.color}1f`, boxShadow: `inset 0 0 0 1px ${meta.color}55` }}>
                  <Icon className="w-4 h-4" style={{ color: meta.color }} aria-hidden />
                </span>
                <span className="nl-geist font-bold text-[9.5px] tracking-[0.08em] leading-tight" style={{ color: meta.color }}>
                  {meta.label}
                </span>
              </span>
              <span className="flex items-end gap-2 mt-2.5">
                <span className="nl-sg font-medium text-[27px] text-[#f2f5fc] leading-none">{s.count}</span>
                {s.added_30d > 0 && (
                  <span className="inline-flex items-center justify-center h-[20px] px-1.5 rounded-md nl-geist font-bold text-[9.5px] mb-0.5"
                    style={{ color: meta.color, backgroundColor: `${meta.color}1c` }}>
                    +{s.added_30d}
                  </span>
                )}
              </span>
            </button>
          );
        })}
      </div>

      {filter && (
        <div className="mt-3.5 flex items-center gap-2.5">
          <p className="nl-geist text-[10px] text-[#9aa3b8]">
            Showing {rows.length} of {allRows.length} tracked records · <span style={{ color: STAGE_META[filter].color }}>{STAGE_META[filter].label}</span>
          </p>
          <button type="button" data-testid="reg-pipeline-clear-filter" onClick={() => setFilter(null)}
            className="nl-geist font-semibold text-[10px] text-[#b88cff] hover:text-white ring-1 ring-[#3d4a70] rounded-full px-2.5 h-5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8b5cff] motion-safe:transition-colors">
            All stages ✕
          </button>
        </div>
      )}

      <div className="mt-5 hidden md:block overflow-x-auto max-w-full">
        <table className="w-full min-w-[880px] border-collapse" data-testid="reg-pipeline-table">
          <thead>
            <tr className="border-b border-white/[0.09]">
              <th scope="col" className="text-left pb-3 pr-3 w-[172px]">
                <button type="button" data-testid="reg-pipeline-sort-jurisdiction" onClick={() => toggleSort("jurisdiction")}
                  aria-sort={sort.key === "jurisdiction" ? (sort.dir === 1 ? "ascending" : "descending") : undefined}
                  className={`${TH} hover:text-white focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#8b5cff] rounded`}>
                  JURISDICTION{arrow("jurisdiction")}
                </button>
              </th>
              <th scope="col" className={`text-left pb-3 pr-3 ${TH}`}>BILL / PROPOSAL</th>
              <th scope="col" className={`text-left pb-3 pr-3 ${TH} w-[168px]`}>CATEGORY</th>
              <th scope="col" className={`text-left pb-3 pr-3 ${TH} w-[168px]`}>STATUS</th>
              <th scope="col" className="text-left pb-3 pr-3 w-[128px]">
                <button type="button" data-testid="reg-pipeline-sort-updated" onClick={() => toggleSort("updated")}
                  aria-sort={sort.key === "updated" ? (sort.dir === 1 ? "ascending" : "descending") : undefined}
                  className={`${TH} hover:text-white focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#8b5cff] rounded`}>
                  LAST UPDATE{arrow("updated")}
                </button>
              </th>
              <th scope="col" className="text-left pb-3 pr-2 w-[118px]">
                <span className="inline-flex items-center gap-1.5">
                  <button type="button" data-testid="reg-pipeline-sort-impact" onClick={() => toggleSort("impact")}
                    aria-sort={sort.key === "impact" ? (sort.dir === 1 ? "ascending" : "descending") : undefined}
                    className={`${TH} hover:text-white focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#8b5cff] rounded`}>
                    IMPACT{arrow("impact")}
                  </button>
                  <InfoTip label="Impact methodology">
                    Impact reflects each record's assessed market significance, assigned by
                    the NewsLayer regulation desk (Critical / High / Medium / Low).
                  </InfoTip>
                </span>
              </th>
              <th scope="col" className="w-8"><span className="sr-only">Open</span></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r: any) => <PipelineRow key={r.slug} r={r} />)}
          </tbody>
        </table>
      </div>
      <div className="mt-4 md:hidden space-y-3">
        {rows.map((r: any) => <PipelineCard key={r.slug} r={r} />)}
      </div>
      {rows.length === 0 && (
        <p className="nl-geist text-[11px] text-[#5b6478] py-8 text-center" data-testid="reg-pipeline-empty">
          No tracked records in this stage yet — counts update as records move through the pipeline.
        </p>
      )}

      <div className="mt-5 pt-4 border-t border-white/[0.06] text-center">
        <Link to="/regulation-tracker" data-testid="reg-pipeline-view-all"
          className="nl-geist font-semibold text-[11px] text-[#b88cff] hover:text-white motion-safe:transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8b5cff] rounded">
          View full regulatory pipeline →
        </Link>
      </div>
    </div>
  );
};

const PipelineRow = ({ r }: { r: any }) => {
  const stage = STAGE_META[r.stage] || STAGE_META.in_review;
  const impact = IMPACT_META[r.impact] || IMPACT_META.medium;
  return (
    <tr data-testid={`reg-pipeline-row-${r.slug}`}
      className="group border-b border-white/[0.06] hover:bg-white/[0.035] motion-safe:transition-colors">
      <td className="py-[15px] pr-3">
        <Link to={`/regulation/${r.slug}`} className="flex items-center gap-2.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8b5cff] rounded">
          <Flag code={r.code} className="w-[26px] h-[18px]" />
          <span className="nl-geist font-semibold text-[11px] text-[#eef1f9] truncate">{r.jurisdiction}</span>
        </Link>
      </td>
      <td className="py-[15px] pr-3">
        <Link to={`/regulation/${r.slug}`} className="block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8b5cff] rounded">
          <span className="block nl-geist font-semibold text-[11.5px] text-[#eef1f9] group-hover:text-[#d6b2ff] line-clamp-1 motion-safe:transition-colors">{r.title}</span>
          {r.summary && <span className="block nl-geist text-[9.5px] text-[#6f7a94] line-clamp-1 mt-0.5">{r.summary}</span>}
        </Link>
      </td>
      <td className="py-[15px] pr-3">
        {r.category ? (
          <span className="inline-flex items-center justify-center h-[23px] px-2.5 rounded-md nl-geist font-semibold text-[9.5px] text-[#c09aff] whitespace-nowrap"
            style={{ backgroundColor: "#a16bff17", boxShadow: "inset 0 0 0 1px #a16bff4d" }}>
            {r.category}
          </span>
        ) : <span className="nl-geist text-[10px] text-[#4c5670]">—</span>}
      </td>
      <td className="py-[15px] pr-3">
        <span className="inline-flex items-center justify-center h-[23px] px-2.5 rounded-md nl-geist font-bold text-[9px] tracking-[0.05em] whitespace-nowrap"
          style={{ color: stage.color, backgroundColor: `${stage.color}17`, boxShadow: `inset 0 0 0 1px ${stage.color}4d` }}
          title={`Official status: ${r.status}`}>
          {stage.label}
        </span>
      </td>
      <td className="py-[15px] pr-3 nl-geist text-[10px] text-[#9aa3b8] whitespace-nowrap">
        Updated {dayjs(r.updated_at).fromNow()}
      </td>
      <td className="py-[15px] pr-2">
        <span className="inline-flex items-center gap-1.5 nl-geist font-semibold text-[10.5px]" style={{ color: impact.color }}>
          {impact.label}
          <span aria-hidden className="tracking-[0.12em] text-[8px]">
            {"●".repeat(impact.dots)}<span className="opacity-30">{"●".repeat(3 - impact.dots)}</span>
          </span>
        </span>
      </td>
      <td className="py-[15px] text-center">
        <Link to={`/regulation/${r.slug}`} aria-label={`Open ${r.title}`}
          className="nl-geist font-semibold text-lg text-[#5b6478] group-hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8b5cff] rounded motion-safe:transition-colors">›</Link>
      </td>
    </tr>
  );
};

const PipelineCard = ({ r }: { r: any }) => {
  const stage = STAGE_META[r.stage] || STAGE_META.in_review;
  const impact = IMPACT_META[r.impact] || IMPACT_META.medium;
  return (
    <Link to={`/regulation/${r.slug}`} data-testid={`reg-pipeline-card-${r.slug}`}
      className="block rounded-xl ring-1 ring-white/[0.08] p-4 hover:ring-[#8b5cff]/70 motion-safe:transition-[box-shadow] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8b5cff]"
      style={CARD_STYLE}>
      <div className="flex items-center gap-2.5">
        <Flag code={r.code} className="w-[26px] h-[18px]" />
        <span className="nl-geist font-semibold text-[10.5px] text-[#eef1f9]">{r.jurisdiction}</span>
        <span className="ml-auto nl-geist font-semibold text-[10px]" style={{ color: impact.color }}>{impact.label}</span>
      </div>
      <p className="nl-geist font-semibold text-[12px] text-[#eef1f9] mt-2.5 leading-snug">{r.title}</p>
      <div className="flex items-center gap-2 mt-3 flex-wrap">
        <span className="inline-flex items-center h-[20px] px-2 rounded nl-geist font-bold text-[8.5px] tracking-[0.05em]"
          style={{ color: stage.color, backgroundColor: `${stage.color}17`, boxShadow: `inset 0 0 0 1px ${stage.color}4d` }}>{stage.label}</span>
        {r.category && (
          <span className="inline-flex items-center h-[20px] px-2 rounded nl-geist font-semibold text-[8.5px] text-[#c09aff]"
            style={{ backgroundColor: "#a16bff17", boxShadow: "inset 0 0 0 1px #a16bff4d" }}>{r.category}</span>
        )}
        <span className="ml-auto nl-geist text-[9px] text-[#9aa3b8]">Updated {dayjs(r.updated_at).fromNow()}</span>
      </div>
    </Link>
  );
};

const DONUT_COLORS = ["#8b5cff", "#14cfe6", "#22d38f", "#f5b420", "#f33db4", "#5b6478"];

export const TrendsPanel = ({ trends }: { trends: any }) => {
  const [hoverCat, setHoverCat] = useState<number | null>(null);
  const rawCats = (trends?.categories || []).filter((c: any) => c.label !== "Other");
  const catTotal = rawCats.reduce((s: number, c: any) => s + c.count, 0);
  const cats = rawCats.map((c: any) => ({ ...c, pct: catTotal ? Math.round((c.count / catTotal) * 100) : 0 }));
  const R = 40, C = 2 * Math.PI * R;
  let acc = 0;
  const segs = cats.map((c: any, i: number) => {
    const frac = Math.max(c.pct, 0) / 100;
    const seg = { i, frac, off: acc };
    acc += frac;
    return seg;
  });
  return (
    <div data-testid="reg-trends" className={`${PANEL} p-5 flex flex-col`} style={PANEL_STYLE}>
      <div className="flex items-center gap-2">
        <h3 className="nl-geist font-bold text-[13px] text-[#eef1f9] tracking-[0.04em]">REGULATORY TREND INSIGHTS</h3>
        <InfoTip label="Trend methodology" testid="reg-trends-info">
          Policy and enforcement activity count NewsLayer policy stories over the last
          30 days vs the prior 30. Category split reflects story topics — derived from coverage.
        </InfoTip>
      </div>
      <div className="grid grid-cols-2 gap-3.5 mt-4">
        {[
          { label: "POLICY ACTIVITY (30D)", value: trends?.policy_30d, delta: trends?.policy_delta_pct, spark: trends?.policy_daily_14d, color: "#8b5cff" },
          { label: "ENFORCEMENT STORIES (30D)", value: trends?.enforcement_30d, delta: trends?.enforcement_delta_pct, spark: trends?.enforcement_daily_14d, color: "#14cfe6" },
        ].map((m) => (
          <div key={m.label} className="rounded-xl p-3.5 pb-2"
            style={{ background: "linear-gradient(180deg, #0a0a10 0%, #050507 100%)", boxShadow: `inset 0 0 0 1px ${m.color}38` }}>
            <p className="nl-geist font-bold text-[8.5px] tracking-[0.08em]" style={{ color: m.color }}>{m.label}</p>
            <div className="flex items-baseline gap-2 mt-2">
              <span className="nl-sg font-medium text-[27px] leading-none text-[#f2f5fc]">{m.value ?? "—"}</span>
              <span className="nl-geist font-semibold text-[10px]" style={{ color: deltaTone(m.delta) }}>
                {m.delta == null ? "" : `${m.delta > 0 ? "+" : ""}${m.delta}%`}
              </span>
            </div>
            <p className="nl-geist text-[8.5px] text-[#6f7a94] mt-0.5">{m.delta == null ? "no prior-period data" : "vs Prior 30D"}</p>
            <AreaSpark points={m.spark || []} color={m.color} className="w-full h-[30px] mt-2" />
          </div>
        ))}
      </div>
      <p className="nl-geist font-bold text-[10px] text-[#c3c9d8] tracking-[0.08em] mt-5">ACTIVITY BY CATEGORY</p>
      <p className="nl-geist text-[8.5px] text-[#6f7a94] mt-0.5">Share of categorized policy coverage · 30D</p>
      <div className="flex items-center gap-5 mt-3">
        <svg viewBox="0 0 100 100" className="w-[104px] h-[104px] shrink-0 -rotate-90" aria-label="Policy activity share by category">
          <circle cx="50" cy="50" r={R} fill="none" stroke="#14141c" strokeWidth="16" />
          {segs.map((s: any) => (
            <circle key={s.i} cx="50" cy="50" r={R} fill="none"
              stroke={DONUT_COLORS[s.i % DONUT_COLORS.length]} strokeWidth={hoverCat === s.i ? 18 : 16}
              strokeDasharray={`${Math.max(s.frac * C - 1.5, 0)} ${C}`} strokeDashoffset={-s.off * C}
              className="motion-safe:transition-[stroke-width] motion-safe:duration-200"
              onMouseEnter={() => setHoverCat(s.i)} onMouseLeave={() => setHoverCat(null)}>
              <title>{cats[s.i]?.label}: {cats[s.i]?.pct}% ({cats[s.i]?.count} stories)</title>
            </circle>
          ))}
        </svg>
        <ul className="flex-1 space-y-[7px] min-w-0">
          {cats.map((c: any, i: number) => (
            <li key={c.label} data-testid={`reg-cat-${i}`}
              onMouseEnter={() => setHoverCat(i)} onMouseLeave={() => setHoverCat(null)}
              className={`flex items-center gap-2 rounded px-1 -mx-1 ${hoverCat === i ? "bg-white/[0.05]" : ""} motion-safe:transition-colors`}>
              <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: DONUT_COLORS[i % DONUT_COLORS.length] }} aria-hidden />
              <span className="nl-geist text-[10px] text-[#c3c9d8] truncate">{c.label}</span>
              <span className="ml-auto nl-geist font-semibold text-[10px] text-[#e5e9f2]">{c.pct}%</span>
            </li>
          ))}
        </ul>
      </div>
      <div className="mt-auto pt-4 text-center">
        <Link to="/regulation-tracker" data-testid="reg-trends-link"
          className="nl-geist font-semibold text-[10px] text-[#b88cff] hover:text-white motion-safe:transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8b5cff] rounded">
          View full regulatory dashboard →
        </Link>
      </div>
    </div>
  );
};

export const MoversPanel = ({ movers }: { movers: any[] }) => (
  <div data-testid="reg-movers" className={`${PANEL} p-5 flex flex-col`} style={PANEL_STYLE}>
    <div className="flex items-center gap-2">
      <h3 className="nl-geist font-bold text-[13px] text-[#eef1f9] tracking-[0.04em]">TOP JURISDICTION MOVERS (30D)</h3>
      <InfoTip label="Movers methodology" testid="reg-movers-info">
        Jurisdictions ranked by change in NewsLayer policy coverage: last 30 days vs the prior 30.
      </InfoTip>
    </div>
    <ol className="mt-2.5 flex-1">
      {movers.map((m, i) => (
        <li key={m.code} className={i > 0 ? "border-t border-white/[0.06]" : ""}>
          <Link to={`/regulation-tracker?country=${encodeURIComponent(m.name)}`} data-testid={`reg-mover-${i}`}
            className="flex items-center gap-3 py-[11px] px-1.5 -mx-1.5 rounded-lg hover:bg-white/[0.04] motion-safe:transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8b5cff]">
            <span className="nl-geist font-bold text-[12px] text-[#8b93a9] w-4">{i + 1}</span>
            <Flag code={m.code} className="w-[28px] h-[19px]" />
            <span className="min-w-0">
              <span className="block nl-geist font-semibold text-[11.5px] text-[#eef1f9] truncate">{m.name}</span>
              <span className="block nl-geist text-[9px] text-[#6f7a94] mt-0.5">
                {(LEVELS[m.level]?.label || "LOW").charAt(0) + (LEVELS[m.level]?.label || "LOW").slice(1).toLowerCase()} activity
              </span>
            </span>
            <span className="ml-auto text-right">
              <span className="block nl-geist font-bold text-[12.5px]" style={{ color: deltaTone(m.delta) }}>
                {m.delta > 0 ? "+" : ""}{m.delta} {m.delta > 0 ? "↗" : m.delta < 0 ? "↘" : ""}
              </span>
              <span className="block nl-geist text-[8.5px] text-[#6f7a94]">stories vs prior</span>
            </span>
          </Link>
        </li>
      ))}
    </ol>
    <div className="pt-3.5 text-center border-t border-white/[0.06]">
      <Link to="/regulation-tracker" data-testid="reg-movers-link"
        className="nl-geist font-semibold text-[10px] text-[#b88cff] hover:text-white motion-safe:transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8b5cff] rounded">
        View full jurisdiction movers →
      </Link>
    </div>
  </div>
);

export const CalendarPanel = ({ calendar }: { calendar: any[] }) => (
  <div data-testid="reg-calendar" className={`${PANEL} p-5 flex flex-col`} style={PANEL_STYLE}>
    <div className="flex items-center gap-2">
      <h3 className="nl-geist font-bold text-[13px] text-[#eef1f9] tracking-[0.04em]">REGULATORY CALENDAR</h3>
      <InfoTip label="Calendar provenance" testid="reg-calendar-info">
        Upcoming dates only, sourced from official record timelines on tracked regulations.
        Each entry links to the full record.
      </InfoTip>
    </div>
    {calendar.length === 0 ? (
      <p className="nl-geist text-[11px] text-[#4c5670] py-8" data-testid="reg-calendar-empty">
        No upcoming verified regulatory events on tracked records.
      </p>
    ) : (
      <ul className="mt-2.5 flex-1">
        {calendar.map((ev, i) => (
          <li key={`${ev.slug}-${ev.date}-${i}`} className={i > 0 ? "border-t border-white/[0.06]" : ""}>
            <Link to={`/regulation/${ev.slug}`} data-testid={`reg-cal-${i}`}
              className="flex items-center gap-4 py-[10px] px-1.5 -mx-1.5 rounded-lg hover:bg-white/[0.04] motion-safe:transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8b5cff]">
              <span className="w-[46px] h-[46px] rounded-[10px] flex flex-col items-center justify-center shrink-0"
                style={{ background: "linear-gradient(180deg,#120e20 0%,#070610 100%)", boxShadow: "inset 0 0 0 1px rgba(139,92,255,0.35)" }}>
                <span className="nl-geist font-bold text-[8.5px] text-[#b88cff] leading-tight uppercase tracking-[0.08em]">{dayjs(ev.date).format("MMM")}</span>
                <span className="nl-sg font-medium text-[15px] text-[#f2f5fc] leading-tight">{dayjs(ev.date).format("DD")}</span>
              </span>
              <span className="min-w-0">
                <span className="block nl-geist font-semibold text-[11.5px] text-[#eef1f9] leading-snug line-clamp-2">{ev.event}</span>
                <span className="block nl-geist text-[9px] text-[#14cfe6] mt-1">
                  {ev.jurisdiction}{ev.past ? ` · ${dayjs(ev.date).format("YYYY")} milestone` : ""}
                </span>
              </span>
            </Link>
          </li>
        ))}
      </ul>
    )}
    <div className="pt-3.5 text-center border-t border-white/[0.06]">
      <Link to="/regulation-tracker" data-testid="reg-calendar-link"
        className="nl-geist font-semibold text-[10px] text-[#b88cff] hover:text-white motion-safe:transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8b5cff] rounded">
        View full regulatory calendar →
      </Link>
    </div>
  </div>
);

const BENEFITS = [
  { Icon: Bell, title: "Real-time Alerts", sub: "Instant policy updates" },
  { Icon: Globe, title: "Jurisdiction Deep Dives", sub: "Comprehensive profiles" },
  { Icon: LineChart, title: "Expert Analysis", sub: "Policy impact & insights" },
  { Icon: Database, title: "Data & Reports", sub: "Downloadable research" },
];

export const PremiumRail = () => {
  const { user } = useAuth();
  const premium = !!user?.is_premium;
  return (
    <div data-testid="reg-premium-rail"
      className="premium-aurora-frame relative overflow-hidden mt-5 rounded-2xl bg-black p-5 flex flex-col xl:flex-row xl:items-center gap-5 shadow-[inset_0_1px_0_rgba(255,255,255,0.03),0_0_60px_-24px_rgba(59,108,246,0.45)]">
      <div aria-hidden className="absolute -top-24 -left-20 h-72 w-72 rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(124,58,237,0.14), transparent 68%)" }} />
      <div aria-hidden className="absolute -bottom-24 -right-16 h-64 w-64 rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(0,229,255,0.08), transparent 70%)" }} />
      <div className="relative flex items-center gap-4 min-w-0 xl:max-w-[380px]">
        <span className="w-[62px] h-[62px] rounded-2xl flex items-center justify-center shrink-0"
          style={{ background: "linear-gradient(180deg,#1c1233 0%,#0f0a1e 100%)", boxShadow: "inset 0 0 0 1px rgba(168,120,255,0.6), 0 0 24px rgba(139,92,255,0.25)" }} aria-hidden>
          <Crown className="w-6 h-6 text-[#e0c9ff]" />
        </span>
        <div className="min-w-0">
          <h3 className="nl-sg font-medium text-[17px] text-[#f0e6ff] tracking-[0.03em] leading-snug">STAY AHEAD OF REGULATORY CHANGE</h3>
          <p className="nl-geist text-[10.5px] text-[#a89fc0] mt-1 leading-snug">
            Unlock real-time alerts, deep-dive analysis, jurisdiction profiles, and exclusive regulatory intelligence.
          </p>
        </div>
      </div>
      <div className="relative grid grid-cols-2 sm:grid-cols-4 gap-3 xl:flex-1">
        {BENEFITS.map(({ Icon, title, sub }) => (
          <div key={title} className="rounded-xl px-3 py-3 flex flex-col items-start gap-2 hover:brightness-115 motion-safe:transition-[filter]"
            style={{ background: "linear-gradient(180deg,#0a0812 0%,#050508 100%)", boxShadow: "inset 0 0 0 1px rgba(110,90,170,0.4)" }}>
            <Icon className="w-4 h-4 text-[#b88cff]" aria-hidden />
            <span className="min-w-0">
              <span className="block nl-geist font-semibold text-[10.5px] text-[#eef1f9] leading-tight">{title}</span>
              <span className="block nl-geist text-[9px] text-[#8d86a3] mt-1">{sub}</span>
            </span>
          </div>
        ))}
      </div>
      <div className="relative shrink-0 flex flex-col items-center gap-2">
        <Link to="/premium" data-testid="reg-premium-cta"
          className={`${premium ? "" : "sparkle-btn "}inline-flex items-center justify-center gap-2 h-[46px] px-7 rounded-lg nl-geist font-bold text-[12px] text-white bg-gradient-to-r from-[#00A6E0] via-[#3B6CF6] to-[#7C3AED] shadow-[0_4px_24px_rgba(59,108,246,0.3)] hover:brightness-110 hover:shadow-[0_6px_30px_rgba(124,58,237,0.4)] motion-safe:transition-[filter,box-shadow] motion-safe:duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70 whitespace-nowrap`}>
          <Crown className="w-4 h-4" aria-hidden /> {premium ? "Full Access Active" : "Unlock Full Access"}
        </Link>
        <Link to="/premium" className="nl-geist text-[9.5px] text-[#b7a3dd] hover:text-white motion-safe:transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#8b5cff] rounded">
          {premium ? "Manage your plan →" : "From $9.99/month →"}
        </Link>
      </div>
    </div>
  );
};
